import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API } from "../App";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Search, MapPin, Calendar, Star, ArrowRight, Utensils, Wine, Music, Coffee, Building2, Navigation, Loader2, Quote, Mail } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { getLocalized } from "../utils/localized";
import { toast } from "sonner";
import { useUserLocation } from "../utils/useUserLocation";

const venueTypeIcons = {
  restaurant: Utensils,
  bar: Wine,
  club: Music,
  cafe: Coffee,
  taverna: Building2,
};

export default function HomePage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [featuredVenues, setFeaturedVenues] = useState([]);
  const [todayEvents, setTodayEvents] = useState([]);
  const [cities, setCities] = useState([]);
  const [venueTypes, setVenueTypes] = useState([]);
  const [popularAreas, setPopularAreas] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geoLoading, setGeoLoading] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { requestLocation } = useUserLocation();

  // Helper: pull a localized field off a homepage block, falling back to EN
  const pickBlock = (blockType, field, fallback = "") => {
    const block = blocks.find((b) => b.block_type === blockType);
    if (!block) return fallback;
    if (lang === "el" && block[`${field}_el`]) return block[`${field}_el`];
    return block[field] || fallback;
  };
  const blockContent = (blockType) =>
    blocks.find((b) => b.block_type === blockType)?.content_json || {};
  const isVisible = (blockType) => {
    // Public /homepage/blocks filters out hidden blocks server-side, so a
    // missing block type == hidden. During initial load (blocks empty) keep
    // everything visible so the page doesn't flash-hide on hydrate.
    if (blocks.length === 0) return true;
    return !!blocks.find((x) => x.block_type === blockType && x.is_visible !== false);
  };

  const handleNearMe = async () => {
    setGeoLoading(true);
    try {
      const next = await requestLocation();
      const { data } = await axios.get(
        `${API}/taxonomy/cities/nearest?lat=${next.lat}&lng=${next.lng}`
      );
      const cityName = getLocalized(data.city, "name", lang);
      toast.success(
        t("home:geo_found", {
          defaultValue: "Closest city: {{city}} ({{km}} km)",
          city: cityName,
          km: data.distance_km,
        })
      );
      navigate(`/venues?city_id=${data.city.id}`);
    } catch (err) {
      const msg =
        err?.message === "unsupported"
          ? t("home:geo_unsupported", { defaultValue: "Your browser does not support geolocation." })
          : err?.code === 1
          ? t("home:geo_denied", { defaultValue: "Location permission denied." })
          : t("home:geo_lookup_failed", { defaultValue: "Could not find a nearby city." });
      toast.error(msg);
    } finally {
      setGeoLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First fetch blocks so we know the featured limit, then parallel-fetch the rest
        const blocksRes = await axios.get(`${API}/homepage/blocks`);
        const blockList = blocksRes.data || [];
        setBlocks(blockList);
        const featuredBlock = blockList.find((b) => b.block_type === "featured_venues");
        const featuredLimit = featuredBlock?.content_json?.limit || 6;

        const [venuesRes, eventsRes, citiesRes, typesRes, areasRes] = await Promise.all([
          axios.get(`${API}/venues?is_featured=true&limit=${featuredLimit}`),
          axios.get(`${API}/events/today?limit=4`),
          axios.get(`${API}/taxonomy/cities`),
          axios.get(`${API}/taxonomy/venue-types`),
          axios.get(`${API}/taxonomy/areas`),
        ]);
        setFeaturedVenues(venuesRes.data);
        setTodayEvents(eventsRes.data);
        setCities(citiesRes.data);
        setVenueTypes(typesRes.data);
        // Curate one hero area per city (first listed) so the chip row is geographically diverse
        const byCity = new Map();
        for (const area of areasRes.data || []) {
          if (!byCity.has(area.city_id)) byCity.set(area.city_id, area);
        }
        setPopularAreas(Array.from(byCity.values()).slice(0, 6));
      } catch (error) {
        console.error("Error fetching homepage data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/venues?search=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate("/venues");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header transparent />

      {/* Hero Section */}
      {isVisible("hero_search") && (
      <section className="relative h-[90vh] min-h-[600px] flex items-center justify-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={blockContent("hero_search").background_image || "https://images.unsplash.com/photo-1675588372093-bc5c2502a78f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxjeXBydXMlMjBiZWFjaCUyMGJhciUyMHN1bnNldHxlbnwwfHx8fDE3NzU4NTk3MzR8MA&ixlib=rb-4.1.0&q=85"}
            alt="Cyprus beach sunset"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-medium text-white mb-6 text-shadow-lg tracking-tight"
          >
            {pickBlock("hero_search", "title", t("home:hero_title"))}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg sm:text-xl text-white/90 mb-10 text-shadow max-w-2xl mx-auto"
          >
            {pickBlock("hero_search", "subtitle", t("home:hero_subtitle"))}
          </motion.p>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onSubmit={handleSearch}
            className="glass rounded-2xl p-2 flex flex-col sm:flex-row gap-2 max-w-2xl mx-auto"
          >
            <div className="flex-1 flex items-center gap-2 px-4">
              <Search className="h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t("home:search_placeholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="border-0 bg-transparent focus-visible:ring-0 text-foreground placeholder:text-muted-foreground"
                data-testid="hero-search-input"
              />
            </div>
            <Button type="submit" size="lg" className="px-8" data-testid="hero-search-button">
              {t("home:search_btn")}
            </Button>
          </motion.form>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap items-center justify-center gap-4 mt-8"
          >
            {venueTypes.slice(0, 5).map((type) => {
              const Icon = venueTypeIcons[type.slug] || Utensils;
              return (
                <Link
                  key={type.id}
                  to={`/venues/type/${type.slug}`}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm hover:bg-white/20 transition-colors"
                  data-testid={`hero-quick-type-${type.slug}`}
                >
                  <Icon className="h-4 w-4" />
                  {getLocalized(type, "name", lang)}
                </Link>
              );
            })}
          </motion.div>

          {/* Popular Areas — hyperlocal discovery */}
          {popularAreas.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="mt-5 flex flex-col items-center gap-2"
              data-testid="hero-area-chip-row"
            >
              <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-widest text-white/70">
                  {t("home:popular_areas", { defaultValue: "Popular areas" })}
                </span>
                <span className="text-white/30">·</span>
                <button
                  type="button"
                  onClick={handleNearMe}
                  disabled={geoLoading}
                  className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-white/90 hover:text-white transition-colors disabled:opacity-60"
                  data-testid="hero-near-me-btn"
                >
                  {geoLoading ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Navigation className="h-3 w-3" />
                  )}
                  {t("home:near_me", { defaultValue: "Venues near me" })}
                </button>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2 px-4">
                {popularAreas.map((area) => (
                  <Link
                    key={area.area_id}
                    to={`/venues?city_id=${area.city_id}&area_id=${area.area_id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.08] backdrop-blur-sm rounded-full text-white text-xs border border-white/20 hover:bg-white/20 hover:border-white/40 transition-colors"
                    data-testid={`hero-area-chip-${area.area_id}`}
                  >
                    <MapPin className="h-3 w-3" />
                    <span>
                      {getLocalized(area, "city_name", lang)} · {getLocalized(area, "name", lang)}
                    </span>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>
      )}

      {/* Featured Venues */}
      {isVisible("featured_venues") && (
      <section className="py-20 px-4" data-testid="featured-venues-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium text-foreground tracking-tight">
                {pickBlock("featured_venues", "title", t("home:featured"))}
              </h2>
              <p className="text-muted-foreground mt-2">{pickBlock("featured_venues", "subtitle", t("home:featured_sub"))}</p>
            </div>
            <Button asChild variant="ghost" className="hidden sm:flex">
              <Link to="/venues" data-testid="featured-venues-view-all">
                {t("home:view_all")} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-muted h-64 rounded-xl mb-4" />
                  <div className="bg-muted h-6 w-2/3 rounded mb-2" />
                  <div className="bg-muted h-4 w-1/2 rounded" />
                </div>
              ))}
            </div>
          ) : featuredVenues.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children">
              {featuredVenues.map((venue) => (
                <VenueCard key={venue.venue_id} venue={venue} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>No featured venues yet. Check back soon!</p>
            </div>
          )}

          <div className="text-center mt-8 sm:hidden">
            <Button asChild variant="outline">
              <Link to="/venues">View all venues</Link>
            </Button>
          </div>
        </div>
      </section>
      )}

      {/* Tonight in Cyprus */}
      {isVisible("tonight_section") && todayEvents.length > 0 && (
        <section className="py-20 px-4 bg-muted" data-testid="tonight-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium text-foreground tracking-tight">
                  {pickBlock("tonight_section", "title", "Tonight in Cyprus")}
                </h2>
                <p className="text-muted-foreground mt-2">{pickBlock("tonight_section", "subtitle", "What's happening right now")}</p>
              </div>
              <Button asChild variant="ghost" className="hidden sm:flex">
                <Link to="/events/today" data-testid="tonight-view-all">
                  View all <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
              {todayEvents.map((event) => (
                <EventCard key={event.event_id} event={event} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Browse by City */}
      {isVisible("browse_by_city") && (
      <section className="py-20 px-4" data-testid="browse-cities-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium text-foreground tracking-tight">
              {pickBlock("browse_by_city", "title", "Browse by City")}
            </h2>
            {pickBlock("browse_by_city", "subtitle") && (
              <p className="text-muted-foreground mt-2">{pickBlock("browse_by_city", "subtitle")}</p>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 stagger-children">
            {cities.map((city) => (
              <Link
                key={city.id}
                to={`/venues/city/${city.slug}`}
                className="group relative overflow-hidden rounded-xl aspect-[4/3] bg-muted"
                data-testid={`city-card-${city.slug}`}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="font-medium text-lg">{city.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Explore by Type */}
      {isVisible("browse_by_venue_type") && (
      <section className="py-20 px-4 bg-muted" data-testid="browse-types-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium text-foreground tracking-tight">
              {pickBlock("browse_by_venue_type", "title", t("home:explore_types", { defaultValue: "Explore by Type" }))}
            </h2>
            {pickBlock("browse_by_venue_type", "subtitle") && (
              <p className="text-muted-foreground mt-2">{pickBlock("browse_by_venue_type", "subtitle")}</p>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 stagger-children">
            {venueTypes.map((type) => {
              const Icon = venueTypeIcons[type.slug] || Utensils;
              return (
                <Link
                  key={type.id}
                  to={`/venues/type/${type.slug}`}
                  className="group flex flex-col items-center p-8 bg-background rounded-xl border border-border hover:border-primary hover:shadow-lg transition-all duration-300"
                  data-testid={`type-card-${type.slug}`}
                >
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <Icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-medium text-foreground">{getLocalized(type, "name", lang)}</h3>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Business CTA */}
      {isVisible("business_cta") && (
      <section className="py-20 px-4" data-testid="business-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium text-foreground tracking-tight mb-4">
            {pickBlock("business_cta", "title", "Own a Venue?")}
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {pickBlock("business_cta", "subtitle", "Join TableCy and reach thousands of customers looking for their next dining experience in Cyprus.")}
          </p>
          <Button asChild size="lg" data-testid="business-cta-button">
            <Link to={blockContent("business_cta").cta_link || "/page/for-venues"}>
              {(lang === "el" && blockContent("business_cta").cta_text_el) || blockContent("business_cta").cta_text || "List Your Venue"}
            </Link>
          </Button>
        </div>
      </section>
      )}

      {/* Admin-added custom blocks (testimonials / sponsored banner / newsletter) */}
      {blocks
        .filter((b) => b.is_visible !== false)
        .filter((b) => ["testimonials", "sponsored_banner", "newsletter_signup"].includes(b.block_type))
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map((b) => (
          <CustomBlock key={b.block_id} block={b} lang={lang} pickBlock={pickBlock} blockContent={blockContent} />
        ))}

      <Footer />
    </div>
  );
}

// Venue Card Component
import VenueCard from "../components/VenueCard";

// Event Card Component
function EventCard({ event }) {
  const eventDate = new Date(event.start_datetime);
  const timeString = eventDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });

  return (
    <Link
      to={`/event/${event.slug}`}
      className="group block bg-background rounded-xl overflow-hidden border border-border hover:border-primary hover:shadow-lg transition-all duration-300"
      data-testid={`event-card-${event.event_id}`}
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        {event.image_url ? (
          <img
            src={event.image_url}
            alt={event.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
            <Calendar className="h-8 w-8 text-muted-foreground" />
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 text-xs text-primary font-medium mb-2">
          <Calendar className="h-3 w-3" />
          <span>Tonight at {timeString}</span>
        </div>
        <h3 className="font-medium text-foreground line-clamp-1 group-hover:text-primary transition-colors">
          {event.title}
        </h3>
        {event.venue_name && (
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {event.venue_name}
          </p>
        )}
      </div>
    </Link>
  );
}

function CustomBlock({ block, lang, pickBlock, blockContent }) {
  const type = block.block_type;
  if (type === "testimonials") {
    const content = block.content_json || {};
    const quotes = Array.isArray(content.quotes) ? content.quotes : [];
    if (quotes.length === 0) return null;
    return (
      <section className="py-20 px-4 bg-muted" data-testid={`custom-block-${block.block_id}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium text-foreground tracking-tight">
              {pickBlock(type, "title", "What guests are saying")}
            </h2>
            {pickBlock(type, "subtitle") && (
              <p className="text-muted-foreground mt-2">{pickBlock(type, "subtitle")}</p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quotes.map((q, idx) => (
              <div
                key={idx}
                className="bg-background rounded-xl border border-border p-6 shadow-sm"
                data-testid={`testimonial-${block.block_id}-${idx}`}
              >
                <Quote className="h-6 w-6 text-primary mb-3" />
                <p className="text-foreground leading-relaxed mb-4">
                  {(lang === "el" && q.text_el) || q.text}
                </p>
                <div className="text-sm">
                  <p className="font-medium text-foreground">{q.author}</p>
                  {q.location && <p className="text-muted-foreground text-xs mt-0.5">{q.location}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (type === "sponsored_banner") {
    const content = block.content_json || {};
    if (!content.image) return null;
    const Wrapper = content.link ? Link : "div";
    const props = content.link ? { to: content.link } : {};
    return (
      <section className="py-12 px-4" data-testid={`custom-block-${block.block_id}`}>
        <div className="max-w-7xl mx-auto">
          <Wrapper
            {...props}
            className="block relative overflow-hidden rounded-2xl group"
            data-testid={`sponsored-banner-${block.block_id}`}
          >
            <img
              src={content.image}
              alt={content.alt_text || pickBlock(type, "title", "Sponsored")}
              className="w-full h-48 md:h-64 object-cover group-hover:scale-[1.02] transition-transform duration-500"
            />
            {(pickBlock(type, "title") || pickBlock(type, "subtitle")) && (
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent flex items-center p-8">
                <div className="max-w-lg text-white">
                  <h3 className="text-2xl md:text-3xl font-medium mb-2">{pickBlock(type, "title")}</h3>
                  {pickBlock(type, "subtitle") && (
                    <p className="text-white/90">{pickBlock(type, "subtitle")}</p>
                  )}
                </div>
              </div>
            )}
          </Wrapper>
        </div>
      </section>
    );
  }

  if (type === "newsletter_signup") {
    return <NewsletterBlock block={block} lang={lang} pickBlock={pickBlock} blockContent={blockContent} />;
  }

  return null;
}

function NewsletterBlock({ block, lang, pickBlock, blockContent }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const content = blockContent(block.block_type);
  const buttonText = (lang === "el" && content.button_text_el) || content.button_text || "Subscribe";
  const successMessage =
    (lang === "el" && content.success_message_el) || content.success_message || "Thanks — you're on the list!";
  const placeholder =
    (lang === "el" && content.placeholder_el) || content.placeholder || "you@example.com";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: email.trim(), source: "homepage" });
      setSubmitted(true);
      setEmail("");
      toast.success(successMessage);
    } catch (err) {
      toast.error("Please enter a valid email address.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-20 px-4 bg-primary/5" data-testid={`custom-block-${block.block_id}`}>
      <div className="max-w-2xl mx-auto text-center">
        <div className="inline-flex h-12 w-12 rounded-full bg-primary/10 items-center justify-center mb-4">
          <Mail className="h-5 w-5 text-primary" />
        </div>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium text-foreground tracking-tight mb-3">
          {pickBlock(block.block_type, "title", "Get the best of Cyprus in your inbox")}
        </h2>
        {pickBlock(block.block_type, "subtitle") && (
          <p className="text-muted-foreground mb-6">{pickBlock(block.block_type, "subtitle")}</p>
        )}
        {submitted ? (
          <p className="text-primary font-medium" data-testid={`newsletter-success-${block.block_id}`}>
            {successMessage}
          </p>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
            data-testid={`newsletter-form-${block.block_id}`}
          >
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={placeholder}
              className="flex-1"
              data-testid={`newsletter-input-${block.block_id}`}
            />
            <Button type="submit" disabled={submitting} data-testid={`newsletter-submit-${block.block_id}`}>
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {buttonText}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}

